import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Target, Eye, Heart, Users, Award, TrendingUp, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

import usePageSeo from '../hooks/usePageSeo';
import JsonLd from '../components/JsonLd';
import { aboutPageSchema, breadcrumbSchema } from '../utils/seoSchemas';
const About = () => {
  usePageSeo({
    title: 'About WPAA | UAE Audit, Tax & Advisory Firm',
    description: 'Learn about White Paper Accounts Auditing — a UAE-based audit, tax and advisory firm with 18+ years of combined expertise serving Mainland and Free Zone businesses.',
    path: '/about'
  });

  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'Upholding the highest ethical standards in every engagement, ensuring trust and transparency with all stakeholders.'
    },
    {
      icon: Award,
      title: 'Quality',
      description: 'Delivering accurate, reliable, and high-quality services that exceed expectations and industry standards.'
    },
    {
      icon: Heart,
      title: 'Confidentiality',
      description: 'Respecting and protecting client information with robust security measures and professional discretion.'
    },
    {
      icon: Users,
      title: 'Professionalism',
      description: 'Maintaining a professional approach in all interactions, communications, and service delivery.'
    },
    {
      icon: Target,
      title: 'Client Focus',
      description: 'Placing client needs at the center of everything we do, ensuring personalized and effective solutions.'
    }
  ];

  const milestones = [
    { year: '2025', title: 'Founded', description: 'White Paper established with vision to transform advisory services in UAE' },
    { year: '200+', title: 'Clients Served', description: 'Growing portfolio of satisfied clients across diverse industries' },
    { year: '98%', title: 'Retention Rate', description: 'Industry-leading client retention demonstrating our commitment to excellence' },
    { year: '100%', title: 'Compliance', description: 'Perfect compliance record across all audit and tax engagements' }
  ];

  return (
    <div className="min-h-screen">
      <JsonLd id="ld-about-page" data={aboutPageSchema()} />
      <JsonLd
        id="ld-about-breadcrumb"
        data={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'About', path: '/about' }
        ])}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 bg-gradient-to-br from-teal-50 via-white to-gray-50">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">About White Paper</span>
            <h1 className="text-5xl font-bold text-gray-900 mt-4 mb-6">
              Building Trust Through Excellence
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Modern, forward-thinking audit, tax, and advisory firm committed to delivering exceptional value and building long-term partnerships with businesses across the UAE.
            </p>
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&h=600&fit=crop&q=75&fm=webp" loading="lazy"
                alt="Team collaboration"
                className="rounded-2xl shadow-xl w-full"
              />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Who We Are</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  White Paper is a UAE-based professional services firm specializing in audit, tax, and advisory services. Founded in 2025, we have quickly established ourselves as a trusted partner for businesses seeking reliable, high-quality financial services.
                </p>
                <p>
                  Our team combines deep expertise in UAE regulatory frameworks with international best practices, enabling us to deliver comprehensive solutions that address the unique challenges of operating in this dynamic market.
                </p>
                <p>
                  We serve a diverse clientele ranging from startups and SMEs to established corporate entities, providing personalized attention and tailored solutions that drive real business value.
                </p>
                <div className="flex flex-wrap gap-4 mt-6">
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="text-teal-600" size={20} />
                    <span className="font-medium">UAE Regulatory Experts</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="text-teal-600" size={20} />
                    <span className="font-medium">Client-Centric Approach</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle2 className="text-teal-600" size={20} />
                    <span className="font-medium">Proven Track Record</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-teal-500 overflow-hidden">
              <div className="bg-gradient-to-br from-teal-600 to-teal-700 p-8 text-white">
                <Target size={48} className="mb-4" />
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-teal-50 leading-relaxed">
                  To deliver comprehensive audit, accounting, and advisory services at competitive rates, upholding the highest standards of quality, ethics, and professionalism, while supporting clients with accurate, compliant, and practical financial solutions.
                </p>
              </div>
            </Card>

            <Card className="border-2 border-blue-500 overflow-hidden">
              <div className="bg-gradient-to-br from-blue-600 to-blue-700 p-8 text-white">
                <Eye size={48} className="mb-4" />
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-blue-50 leading-relaxed">
                  To be the most trusted and sought-after audit, tax, and advisory firm in the UAE, recognized for our integrity, expertise, and unwavering commitment to client success and business excellence.
                </p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <span className="text-teal-600 font-semibold text-sm uppercase tracking-wider">Our Foundation</span>
            <h2 className="text-4xl font-bold text-gray-900 mt-3 mb-4">
              Core Values That Guide Us
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              These principles shape our culture, guide our decisions, and define how we serve our clients.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card key={index} className="border-2 hover:border-teal-500 transition-all hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className="bg-teal-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                      <Icon className="text-teal-600 group-hover:text-white transition-colors" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Milestones */}
      <section className="py-20 px-4 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Journey in Numbers</h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              Key achievements and milestones that reflect our growth and commitment to excellence.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {milestones.map((milestone, index) => (
              <div key={index} className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 hover:bg-white/20 transition-all">
                  <p className="text-5xl font-bold text-teal-400 mb-3">{milestone.year}</p>
                  <h3 className="text-xl font-bold mb-2">{milestone.title}</h3>
                  <p className="text-gray-300 text-sm">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-20 px-4 bg-white">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Makes Us Different</h2>
            <div className="space-y-6">
              <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                  <TrendingUp className="text-teal-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Competitive Pricing</h3>
                  <p className="text-gray-600">Premium services at rates designed for businesses of all sizes, ensuring exceptional value without compromising on quality.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                  <Users className="text-teal-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Personalized Attention</h3>
                  <p className="text-gray-600">Direct access to senior professionals who understand your business and provide customized solutions tailored to your needs.</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-teal-50 transition-colors">
                <div className="bg-teal-100 p-3 rounded-lg flex-shrink-0">
                  <Award className="text-teal-600" size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">UAE Market Expertise</h3>
                  <p className="text-gray-600">Deep understanding of local regulations, tax laws, and business environment with up-to-date knowledge of latest regulatory changes.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4 bg-gradient-to-r from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Partner With Us</h2>
          <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
            Experience the White Paper difference. Let's discuss how we can support your business goals.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule Your Free Consultation
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
